import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Trash2, UserCog, CheckCircle2, Clock, XCircle,
  Award, BadgeCheck, X, Loader2,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";

const PERMISSIONS = [
  { id: "internships", label: "Manage Internships" },
  { id: "tests", label: "Manage Test Series & Exams" },
  { id: "payments", label: "View Payments" },
  { id: "gallery", label: "Manage Gallery" },
];

export default function AdminVolunteers() {
  const { formatApiError } = useAuth();
  const [items, setItems] = useState([]);
  const [requests, setRequests] = useState([]);
  const [tab, setTab] = useState("approved"); // pending | approved
  const [memberUser, setMemberUser] = useState(null);
  const [memberForm, setMemberForm] = useState({
    member_id: "", member_role_label: "", permissions: [], notify_email: true,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { load(); }, []);
  const load = async () => {
    const [all, pend] = await Promise.all([
      api.get("/admin/users?role=volunteer"),
      api.get("/admin/volunteer-requests"),
    ]);
    setItems((all.data || []).filter((u) => u.is_volunteer_approved));
    setRequests(pend.data || []);
  };

  const approve = async (id) => {
    try {
      await api.put(`/admin/users/${id}/approve-volunteer`);
      toast.success("Volunteer approved — email sent");
      load();
    } catch { toast.error("Failed"); }
  };
  const reject = async (u) => {
    const reason = window.prompt(`Reject volunteer request from ${u.name}?\n\nEnter a short reason (will be emailed to the applicant):`, "We are not onboarding new volunteers at the moment.");
    if (reason === null) return;
    if (!reason.trim()) { toast.error("Reason is required"); return; }
    await api.delete(`/admin/users/${u.id}?reason=${encodeURIComponent(reason.trim())}`);
    toast.success("Request rejected — email sent");
    load();
  };
  const togglePerm = async (user, perm) => {
    const permissions = user.permissions || [];
    const next = permissions.includes(perm) ? permissions.filter((p) => p !== perm) : [...permissions, perm];
    try {
      await api.put(`/admin/users/${user.id}`, { permissions: next });
      toast.success("Permissions updated");
      load();
    } catch { toast.error("Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete volunteer?")) return;
    await api.delete(`/admin/users/${id}`);
    toast.success("Deleted");
    load();
  };

  const openMember = (u) => {
    setMemberUser(u);
    setMemberForm({
      member_id: u.member_id || "",
      member_role_label: u.member_role_label || "",
      permissions: u.permissions || [],
      notify_email: !u.member_id,
    });
  };

  const togglePermInModal = (key) => {
    setMemberForm((f) => ({
      ...f,
      permissions: f.permissions.includes(key)
        ? f.permissions.filter((p) => p !== key)
        : [...f.permissions, key],
    }));
  };

  const submitMember = async () => {
    const id = (memberForm.member_id || "").trim();
    if (id.length < 2) { toast.error("Member ID is required"); return; }
    setSubmitting(true);
    try {
      await api.put(`/admin/users/${memberUser.id}/member-id`, {
        member_id: id,
        member_role_label: memberForm.member_role_label,
        permissions: memberForm.permissions,
        notify_email: memberForm.notify_email,
      });
      toast.success(memberUser.member_id ? "Member ID updated" : "Member ID assigned");
      setMemberUser(null);
      load();
    } catch (e) {
      toast.error(formatApiError?.(e.response?.data?.detail) || "Failed");
    } finally { setSubmitting(false); }
  };

  const revokeMember = async (u) => {
    if (!window.confirm(`Revoke Member ID ${u.member_id} from ${u.name}?\n\nNote: this clears the Member ID but does NOT remove the granted module permissions. You can adjust permissions below.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}/member-id`);
      toast.success("Member ID revoked");
      load();
    } catch { toast.error("Failed to revoke"); }
  };

  return (
    <DashboardLayout>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="heading text-2xl font-black">Volunteers</h1>
        <div className="flex gap-1 bg-slate-100 p-1 rounded-xl">
          <button
            onClick={() => setTab("pending")}
            className={`px-4 py-2 rounded-lg text-sm font-bold inline-flex items-center gap-2 ${tab === "pending" ? "bg-white shadow text-brand" : "text-slate-600"}`}
            data-testid="tab-pending"
          >
            <Clock size={14} /> Pending ({requests.length})
          </button>
          <button
            onClick={() => setTab("approved")}
            className={`px-4 py-2 rounded-lg text-sm font-bold inline-flex items-center gap-2 ${tab === "approved" ? "bg-white shadow text-brand" : "text-slate-600"}`}
            data-testid="tab-approved"
          >
            <CheckCircle2 size={14} /> Approved ({items.length})
          </button>
        </div>
      </div>

      {tab === "pending" ? (
        <div className="mt-6 grid lg:grid-cols-2 gap-5">
          {requests.length === 0 && (
            <div className="p-10 rounded-xl border border-dashed border-slate-300 text-center col-span-full">
              <Clock size={36} className="mx-auto text-slate-300" />
              <div className="mt-3 text-slate-500">No pending volunteer requests.</div>
            </div>
          )}
          {requests.map((u) => (
            <div key={u.id} className="p-5 rounded-xl border-2 border-amber-200 bg-amber-50/30" data-testid={`vol-request-${u.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="heading font-black text-lg">{u.name}</div>
                  <div className="text-xs text-slate-600">{u.email} · {u.phone || "No phone"}</div>
                  <div className="mt-2 text-xs text-slate-500">Submitted: {new Date(u.created_at).toLocaleString()}</div>
                </div>
                <span className="text-[10px] font-bold uppercase px-2 py-1 rounded bg-amber-200 text-amber-900">Pending</span>
              </div>
              {u.skills && (
                <div className="mt-3 p-3 rounded-lg bg-white border border-slate-200 text-sm">
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-1">Motivation</div>
                  {u.skills}
                </div>
              )}
              <div className="mt-4 flex gap-2">
                <button onClick={() => approve(u.id)} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-lg py-2 text-sm inline-flex items-center justify-center gap-1" data-testid={`approve-vol-${u.id}`}>
                  <CheckCircle2 size={14} /> Approve
                </button>
                <button onClick={() => reject(u)} className="bg-red-50 hover:bg-red-100 text-red-600 font-bold rounded-lg px-4 py-2 text-sm inline-flex items-center gap-1" data-testid={`reject-vol-${u.id}`}>
                  <XCircle size={14} /> Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-6 grid lg:grid-cols-2 gap-5">
          {items.length === 0 && (
            <div className="p-10 rounded-xl border border-dashed border-slate-300 text-center col-span-full">
              <UserCog size={36} className="mx-auto text-slate-300" />
              <div className="mt-3 text-slate-500">No approved volunteers yet.</div>
            </div>
          )}
          {items.map((u) => (
            <div key={u.id} className="p-5 rounded-xl border border-slate-200 bg-white" data-testid={`volunteer-card-${u.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="heading font-black text-lg truncate">{u.name}</div>
                    {u.member_id && (
                      <span className="text-[9px] font-black uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded inline-flex items-center gap-0.5" data-testid={`pmr-member-badge-${u.id}`}>
                        <BadgeCheck size={10} /> PMR Member
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">{u.email} · {u.phone || "No phone"}</div>
                  {u.referral_code && (
                    <div className="text-[11px] text-slate-600 mt-1 inline-flex items-center gap-1.5">
                      <span className="text-slate-400 font-bold uppercase tracking-wider">Referral:</span>
                      <span className="font-mono font-bold text-brand bg-blue-50 px-1.5 py-0.5 rounded" data-testid={`vol-referral-${u.id}`}>{u.referral_code}</span>
                    </div>
                  )}
                  {u.member_id && (
                    <div className="mt-1 text-xs" data-testid={`member-id-cell-${u.id}`}>
                      <span className="font-mono font-bold text-emerald-700">{u.member_id}</span>
                      {u.member_role_label && <span className="text-slate-500"> · {u.member_role_label}</span>}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => openMember(u)}
                    className="text-emerald-700 hover:bg-emerald-50 p-2 rounded"
                    title={u.member_id ? "Edit Member" : "Assign Member ID"}
                    data-testid={`assign-member-${u.id}`}
                  >
                    <Award size={14} />
                  </button>
                  {u.member_id && (
                    <button
                      onClick={() => revokeMember(u)}
                      className="text-amber-600 hover:bg-amber-50 p-2 rounded"
                      title="Revoke Member ID"
                      data-testid={`revoke-member-${u.id}`}
                    >
                      <X size={14} />
                    </button>
                  )}
                  <button onClick={() => del(u.id)} className="text-red-600 hover:bg-red-50 p-2 rounded" data-testid={`delete-volunteer-${u.id}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <div className="mt-4">
                <div className="text-xs font-black uppercase tracking-[0.2em] text-slate-500 mb-2">Permissions</div>
                <div className="grid grid-cols-2 gap-2">
                  {PERMISSIONS.map((p) => {
                    const has = (u.permissions || []).includes(p.id);
                    return (
                      <label key={p.id} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer ${has ? "border-brand bg-blue-50" : "border-slate-200"}`}>
                        <input
                          type="checkbox"
                          checked={has}
                          onChange={() => togglePerm(u, p.id)}
                          className="accent-brand"
                          data-testid={`volunteer-perm-${u.id}-${p.id}`}
                        />
                        <span className="text-xs font-semibold">{p.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Member ID modal */}
      <Dialog open={!!memberUser} onOpenChange={(o) => !o && setMemberUser(null)}>
        <DialogContent className="max-w-lg" data-testid="member-id-modal">
          <DialogHeader>
            <DialogTitle className="inline-flex items-center gap-2">
              <BadgeCheck size={18} className="text-emerald-600" />
              {memberUser?.member_id ? "Edit Member" : "Assign PMR Member ID"} — {memberUser?.name}
            </DialogTitle>
            <DialogDescription>
              Member IDs are reserved for PMR Welfare Foundation organisation members. Admin decides what permissions the member gets.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Member ID *</label>
              <input
                value={memberForm.member_id}
                onChange={(e) => setMemberForm({ ...memberForm, member_id: e.target.value.toUpperCase() })}
                placeholder="PMR-MEM-001"
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-emerald-600 outline-none text-sm font-mono"
                data-testid="member-id-input"
              />
              <p className="text-[10px] text-slate-400 mt-1">Unique across the platform.</p>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Member Role / Title</label>
              <input
                value={memberForm.member_role_label}
                onChange={(e) => setMemberForm({ ...memberForm, member_role_label: e.target.value })}
                placeholder="Field Coordinator, Volunteer Lead, etc."
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm"
                data-testid="member-role-label-input"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Module Access</label>
              <div className="mt-2 space-y-2">
                {PERMISSIONS.map((p) => (
                  <label key={p.id} className="flex items-start gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={memberForm.permissions.includes(p.id)}
                      onChange={() => togglePermInModal(p.id)}
                      className="mt-1"
                      data-testid={`member-perm-${p.id}`}
                    />
                    <div>
                      <div className="font-semibold text-slate-800 text-xs uppercase tracking-wider">{p.id}</div>
                      <div className="text-xs text-slate-500">{p.label}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={memberForm.notify_email}
                onChange={(e) => setMemberForm({ ...memberForm, notify_email: e.target.checked })}
                data-testid="member-notify-email"
              />
              Email the user about their Member ID
            </label>
          </div>
          <DialogFooter>
            <button onClick={() => setMemberUser(null)} className="px-4 py-2 text-sm font-semibold" data-testid="member-id-cancel">Cancel</button>
            <button
              onClick={submitMember}
              disabled={submitting}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-lg px-5 py-2 text-sm disabled:opacity-60 inline-flex items-center gap-2"
              data-testid="member-id-submit"
            >
              {submitting && <Loader2 size={14} className="animate-spin" />}
              {memberUser?.member_id ? "Update Member" : "Assign Member ID"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
